// frontend/src/components/FuturisticSearchBar.jsx

import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import {
  Search, MapPin, Calendar, X, Check,
  ArrowRight, User, Mail, Phone, ChevronLeft, ChevronRight, ChevronDown
} from 'lucide-react';
import { GET_COUNTRIES_BY_CONTINENT } from '../graphql/tourBuilder';



/* ─── Design tokens ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.15)';
const NAVY     = '#080e1a';

const SearchStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@300;400;500;600&display=swap');
    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }
    .search-pill { background: #fff; border: 1px solid #ddd9d2; box-shadow: 0 12px 48px -8px rgba(0,0,0,0.14), 0 4px 16px -4px rgba(0,0,0,0.08); }
    .seg-btn { display: flex; align-items: center; gap: 14px; padding: 16px 22px; cursor: pointer; background: transparent; text-align: left; width: 100%; transition: background 0.2s; }
    .seg-btn:hover { background: #FAFAF8; }
    .seg-btn.open  { background: #FAFAF8; }
    .seg-icon { width: 36px; height: 36px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border: 1px solid rgba(184,151,90,0.2); }
    .seg-divider { width: 1px; height: 32px; background: #e2ddd6; flex-shrink: 0; }
    .seg-dropdown { position: absolute; top: calc(100% + 8px); z-index: 200; background: #fff; border: 1px solid #ddd9d2; box-shadow: 0 24px 64px -12px rgba(0,0,0,0.18); }
    .cont-tab { font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; padding: 9px 14px; border: 1px solid transparent; background: transparent; cursor: pointer; white-space: nowrap; color:#12100e; transition: all 0.2s; }
    .cont-tab:hover { color:#12100e; }
    .cont-tab.active { border-color: ${GOLD}; color: ${NAVY}; background: ${GOLD_DIM}; }
    .country-chip { font-family: 'Jost', sans-serif; font-size:13px; font-weight: 500; letter-spacing: 0.1em; padding: 9px 14px; border: 1px solid #e2ddd6; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px; color:#12100e; transition: all 0.2s; white-space: nowrap; }
    .country-chip:hover { border-color: #b0aaa0; color: ${NAVY}; }
    .country-chip.selected { border-color: ${GOLD}; background: ${GOLD_DIM}; color: ${NAVY}; }
    .cal-day { aspect-ratio: 1; display: flex; align-items: center; justify-content: center; font-family: 'Jost', sans-serif; font-size:15px; font-weight: 400; cursor: pointer; transition: all 0.2s; color:#12100e; }
    .cal-day:hover:not(:disabled) { background: #f7f5f2; color: ${NAVY}; }
    .cal-day.selected { background: ${NAVY}; color: #fff; }
    .cal-day.in-range { background: ${GOLD_DIM}; color: ${NAVY}; }
    .cal-day:disabled { color: #d1cdc7; cursor: default; }
    .search-cta { position: relative; overflow: hidden; width: 56px; height: 56px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: ${NAVY}; border: 1px solid ${NAVY}; cursor: pointer; transition: all 0.35s cubic-bezier(0.22,1,0.36,1); }
    .search-cta::before { content: ''; position: absolute; inset: 0; background: ${GOLD}; transform: scaleX(0); transform-origin: left; transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0; }
    .search-cta:hover::before { transform: scaleX(1); }
    .search-cta svg { position: relative; z-index: 1; transition: color 0.35s; }
    .search-cta:hover svg { color: ${NAVY}; }
    .gold-rule { height: 1px; background: linear-gradient(90deg, ${GOLD} 0%, rgba(184,151,90,0.1) 100%); }
    .drawer-panel { position: absolute; top: 0; right: 0; height: 100%; width: 100%; max-width: 400px; background: #fff; border-left: 1px solid #e2ddd6; display: flex; flex-direction: column; transition: transform 0.45s cubic-bezier(0.22,1,0.36,1); box-shadow: -24px 0 80px rgba(0,0,0,0.12); }
    .drawer-field { width: 100%; background: transparent; font-family: 'Jost', sans-serif; font-size:15px; font-weight: 400; color: #0f172a; border: none; border-bottom: 1px solid #ddd9d2; padding: 12px 0 12px 32px; outline: none; transition: border-color 0.25s; }
    .drawer-field:focus { border-bottom-color: ${GOLD}; }
    .drawer-field::placeholder { color: #b8b2aa; }
    .drawer-cta { position: relative; overflow: hidden; width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px; font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; padding: 16px 28px; background: ${NAVY}; color: #fff; border: 1px solid ${NAVY}; cursor: pointer; transition: all 0.35s cubic-bezier(0.22,1,0.36,1); }
    .drawer-cta::before { content: ''; position: absolute; inset: 0; background: ${GOLD}; transform: scaleX(0); transform-origin: left; transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0; }
    .drawer-cta:hover::before { transform: scaleX(1); }
    .drawer-cta:hover { color: ${NAVY}; border-color: ${GOLD}; }
    .drawer-cta span, .drawer-cta svg { position: relative; z-index: 1; }
    .tag-badge { font-family: 'Jost', sans-serif; font-size:12px; font-weight: 600; letter-spacing:0.16em; text-transform: uppercase; padding: 4px 10px; background: ${GOLD_DIM}; border: 1px solid rgba(184,151,90,0.3); color: ${GOLD}; display: inline-flex; align-items: center; }
    @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
    .fade-in { animation: fadeIn 0.25s cubic-bezier(0.22,1,0.36,1) both; }
    .picker-cell { font-family: 'Jost', sans-serif; font-size:13px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase; padding: 10px 6px; text-align: center; cursor: pointer; border: 1px solid transparent; transition: all 0.2s; color:#12100e; }
    .picker-cell:hover { border-color: #b0aaa0; color: ${NAVY}; }
    .picker-cell.active { border-color: ${GOLD}; background: ${GOLD_DIM}; color: ${NAVY}; }
    .picker-cell.disabled { color: #d1cdc7; cursor: default; pointer-events: none; }
    .cal-header-label { font-family: 'Cormorant Garamond', serif; font-size:25px; font-weight: 300; font-style:normal; color: #1e293b; cursor: pointer; transition: color 0.2s; border-bottom: 1px solid transparent; padding-bottom: 1px; background: none; border-top: none; border-left: none; border-right: none; outline: none; }
    .cal-header-label:hover { color: ${GOLD}; border-bottom-color: ${GOLD}; }
  `}</style>
);

/* ─── Build continent structure from API countries ─── */
const buildContinents = (countries) => {
  const map = {};
  countries.forEach(c => {
    const slug = c.frontendContinentSlug;
    if (!slug) return;
    if (!map[slug]) map[slug] = {
      id:        slug,
      name:      c.continentName || slug.charAt(0).toUpperCase() + slug.slice(1),
      countries: [],
    };
    map[slug].countries.push(c.name);
  });
  return Object.values(map);
};

const ProfessionalSearchBar = forwardRef((props, ref) => {
  const navigate = useNavigate();

  const { data: countriesData, loading: countriesLoading } = useQuery(GET_COUNTRIES_BY_CONTINENT, {
    fetchPolicy: 'cache-first',
  });

  const continents = useMemo(() =>
    countriesData?.tourCountries?.length
      ? buildContinents(countriesData.tourCountries)
      : [],
    [countriesData?.tourCountries]
  );

  const [whereOpen,  setWhereOpen]  = useState(false);
  const [whenOpen,   setWhenOpen]   = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Mounted only while open/closing — a permanently-mounted fixed+translateX
  // panel still contributes to the page's scrollable width even when
  // off-screen, which caused unwanted horizontal scroll and broke the
  // sticky topbar on mobile.
  const [drawerRendered, setDrawerRendered] = useState(false);
  const openDrawer = () => { setDrawerRendered(true); setDrawerOpen(true); };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setDrawerRendered(false), 450);
  };

  const [selectedContinent, setSelectedContinent] = useState('africa');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedYear,      setSelectedYear]      = useState(new Date().getFullYear());
  const [selectedMonth,     setSelectedMonth]     = useState(new Date().getMonth());
  const [startDate,         setStartDate]         = useState(null);
  const [endDate,           setEndDate]           = useState(null);

  /* ── Guest data: name, email, phone ── */
  const [userData, setUserData] = useState({ name: '', email: '', phone: '' });

  const [calView, setCalView] = useState('days');

  const containerRef = useRef(null);
  const whereRef     = useRef(null);
  const whenRef      = useRef(null);

  

  /* Auto-select first available continent once data loads */
  useEffect(() => {
    if (continents.length > 0 && !continents.find(c => c.id === selectedContinent)) {
      setSelectedContinent(continents[0].id);
    }
  }, [continents, selectedContinent]);

  useImperativeHandle(ref, () => ({
    handleExternalTrigger() {
      if (selectedCountries.length === 0) {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => setWhereOpen(true), 400);
      } else {
        openDrawer();
      }
    }
  }));

  useEffect(() => {
    const handleOutside = (e) => {
      if (whereRef.current && !whereRef.current.contains(e.target)) setWhereOpen(false);
      if (whenRef.current  && !whenRef.current.contains(e.target))  setWhenOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const getMonthDays = (y, m) => {
    const days = [];
    const first = new Date(y, m, 1);
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    for (let i = 1; i <= new Date(y, m + 1, 0).getDate(); i++) days.push(new Date(y, m, i));
    return days;
  };

  const today = new Date(); today.setHours(0,0,0,0);
  const isDisabled = d => !d || d < today;

  const handleDateClick = (date) => {
    if (isDisabled(date)) return;
    if (!startDate || (startDate && endDate)) {
      setStartDate(date); setEndDate(null);
    } else {
      if (date < startDate) { setEndDate(startDate); setStartDate(date); }
      else setEndDate(date);
      setTimeout(() => setWhenOpen(false), 280);
    }
  };

  const canGoPrev = () => {
    if (calView === 'years')  return selectedYear - 5 > today.getFullYear();
    if (calView === 'months') return selectedYear > today.getFullYear();
    if (selectedYear < today.getFullYear()) return false;
    return !(selectedYear === today.getFullYear() && selectedMonth <= today.getMonth());
  };

  const prevMonth = () => {
    if (calView === 'years')  { setSelectedYear(y => y - 10); return; }
    if (calView === 'months') { if (selectedYear > today.getFullYear()) setSelectedYear(y => y - 1); return; }
    if (!canGoPrev()) return;
    if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(y => y - 1); }
    else setSelectedMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (calView === 'years')  { setSelectedYear(y => y + 10); return; }
    if (calView === 'months') { setSelectedYear(y => y + 1); return; }
    if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(y => y + 1); }
    else setSelectedMonth(m => m + 1);
  };

  const yearGridStart = Math.floor(selectedYear / 10) * 10;
  const toggleCountry = (c) =>
    setSelectedCountries(p => p.includes(c) ? p.filter(x => x !== c) : [...p, c]);

  const [errors, setErrors] = useState({ name: '', email: '', phone: '' });

  const validate = () => {
    const errs = { name: '', email: '', phone: '' };
    
    if (!userData.name.trim()) {
      errs.name = 'Full name is required';
    } else if (userData.name.trim().length < 2) {
      errs.name = 'Please enter your full name';
    }
  
    if (!userData.email.trim()) {
      errs.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email.trim())) {
      errs.email = 'Please enter a valid email address';
    }
  
    if (!userData.phone.trim()) {
      errs.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-().]{7,20}$/.test(userData.phone.trim())) {
      errs.phone = 'Please enter a valid phone number';
    }
  
    setErrors(errs);
    return !errs.name && !errs.email && !errs.phone;
  };
  
  const handleLaunch = (e) => {
    e.preventDefault();
    if (!validate()) return;   // ← stop here if invalid
    if (props.onClose) props.onClose();
    navigate('/tour-builder', {
      state: {
        searchData: { countries: selectedCountries, startDate, endDate },
        user: { name: userData.name, email: userData.email, phone: userData.phone },
      },
    });
  };

  const destLabel = selectedCountries.length > 0
    ? selectedCountries.slice(0, 2).join(', ') + (selectedCountries.length > 2 ? ` +${selectedCountries.length - 2}` : '')
    : 'Where are you going?';

  const dateLabel = startDate
    ? `${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}${endDate ? ` – ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ' – …'}`
    : 'When are you travelling?';

  const datesReady  = !!(startDate && endDate);
  const searchReady = selectedCountries.length > 0 && datesReady;

  return (
    <div ref={containerRef} className="f-jost w-full max-w-4xl mx-auto">
      <SearchStyles />

      <div className="search-pill">
        <div className="flex flex-col md:flex-row items-stretch">

          {/* ── Destination segment (Converts outer element from <button> to <div>) ── */}
          <div ref={whereRef} className="relative flex-[1.3]">
            <div
              onClick={() => { setWhereOpen(w => !w); setWhenOpen(false); }}
              className={`seg-btn ${whereOpen ? 'open' : ''}`}
              style={{ borderRight: '1px solid #e8e4de' }}
            >
              <div className="seg-icon">
                <MapPin size={14} strokeWidth={1.5} style={{ color: GOLD }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: GOLD }}>Destination</p>
                <p className={`f-jost text-[15px] truncate ${selectedCountries.length ? 'text-slate-800 font-medium' : 'text-slate-400 font-light'}`}>
                  {destLabel}
                </p>
              </div>
              {selectedCountries.length > 0 && (
                <button
                  onClick={e => { e.stopPropagation(); setSelectedCountries([]); }}
                  className="p-1 text-slate-300 hover:text-slate-600 transition-colors flex-shrink-0"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            {whereOpen && (
              <div className="seg-dropdown left-0 w-full md:w-[540px] p-6 fade-in">
                {countriesLoading && (
                  <p className="f-jost text-[15px] text-slate-400 font-light py-2">
                    Loading destinations…
                  </p>
                )}

                {!countriesLoading && continents.length > 0 && (
                  <>
                    <div className="flex flex-wrap gap-2 mb-5">
                      {continents.map(c => (
                        <button
                          key={c.id}
                          onClick={() => setSelectedContinent(c.id)}
                          className={`cont-tab ${selectedContinent === c.id ? 'active' : ''}`}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                    <div className="gold-rule mb-5" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {continents.find(c => c.id === selectedContinent)?.countries.map(country => (
                        <button
                          key={country}
                          onClick={() => toggleCountry(country)}
                          className={`country-chip ${selectedCountries.includes(country) ? 'selected' : ''}`}
                        >
                          <span>{country}</span>
                          {selectedCountries.includes(country) && (
                            <Check size={11} strokeWidth={2.5} style={{ color: GOLD, flexShrink: 0 }} />
                          )}
                        </button>
                      ))}
                    </div>
                    {selectedCountries.length > 0 && (
                      <div className="flex items-center justify-between mt-5 pt-4 border-t" style={{ borderColor: '#f0ece6' }}>
                        <span className="f-jost text-[13px] text-slate-500">
                          {selectedCountries.length} destination{selectedCountries.length > 1 ? 's' : ''} selected
                        </span>
                        <button
                          onClick={() => { setWhereOpen(false); setWhenOpen(true); }}
                          className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] flex items-center gap-2"
                          style={{ color: GOLD }}
                        >
                          Set dates <ArrowRight size={11} />
                        </button>
                      </div>
                    )}
                  </>
                )}

                {!countriesLoading && continents.length === 0 && (
                  <p className="f-jost text-[15px] text-slate-400 font-light py-2">
                    No destinations available yet.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ── Date segment (Converts outer element from <button> to <div>) ── */}
          <div ref={whenRef} className="relative flex-1">
            <div
              onClick={() => { setWhenOpen(w => !w); setWhereOpen(false); if (!whenOpen) setCalView('days'); }}
              className={`seg-btn ${whenOpen ? 'open' : ''}`}
            >
              <div className="seg-icon">
                <Calendar size={14} strokeWidth={1.5} style={{ color: GOLD }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: GOLD }}>
                  Travel Dates
                  {!datesReady && (
                    <span className="ml-2 normal-case tracking-normal font-normal" style={{ color: '#f87171', fontSize: '12px' }}>
                      · required
                    </span>
                  )}
                </p>
                <p className={`f-jost text-[15px] truncate ${startDate ? 'text-slate-800 font-medium' : 'text-slate-400 font-light'}`}>
                  {dateLabel}
                </p>
              </div>
              {startDate && (
                <button
                  onClick={e => { e.stopPropagation(); setStartDate(null); setEndDate(null); }}
                  className="p-1 text-slate-300 hover:text-slate-600 transition-colors flex-shrink-0"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            {whenOpen && (
              <div className="seg-dropdown right-0 w-full md:w-[320px] p-6 fade-in">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex flex-col gap-1">
                    <button
                      className="cal-header-label"
                      onClick={() => setCalView(v => v === 'days' ? 'months' : v === 'months' ? 'years' : 'days')}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {calView === 'years'
                          ? `${yearGridStart} – ${yearGridStart + 9}`
                          : calView === 'months'
                            ? selectedYear
                            : `${months[selectedMonth]} ${selectedYear}`}
                        <ChevronDown
                          size={13}
                          strokeWidth={1.5}
                          style={{
                            color: GOLD,
                            transition: 'transform 0.25s',
                            transform: calView !== 'days' ? 'rotate(180deg)' : 'rotate(0deg)',
                            flexShrink: 0,
                          }}
                        />
                      </span>
                    </button>
                    <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: '#12100e' }}>
                      {calView === 'days'
                        ? 'tap to pick month or year'
                        : calView === 'months'
                          ? 'tap again to pick year'
                          : 'select a year to continue'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={prevMonth}
                      disabled={calView === 'days' && !canGoPrev()}
                      className="w-7 h-7 flex items-center justify-center border transition-all"
                      style={{
                        borderColor: '#e2ddd6',
                        color: (calView === 'days' && !canGoPrev()) ? '#d1cdc7' : '#475569',
                      }}
                    >
                      <ChevronLeft size={12} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={nextMonth}
                      className="w-7 h-7 flex items-center justify-center border transition-all"
                      style={{ borderColor: '#e2ddd6', color: '#12100e' }}
                    >
                      <ChevronRight size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>

                {calView === 'years' && (
                  <div className="grid grid-cols-4 gap-1 fade-in">
                    {Array.from({ length: 12 }, (_, i) => yearGridStart - 1 + i).map(yr => {
                      const isPast = yr < today.getFullYear();
                      return (
                        <button
                          key={yr}
                          disabled={isPast}
                          onClick={() => { setSelectedYear(yr); setCalView('months'); }}
                          className={`picker-cell ${yr === selectedYear ? 'active' : ''} ${isPast ? 'disabled' : ''}`}
                        >
                          {yr}
                        </button>
                      );
                    })}
                  </div>
                )}

                {calView === 'months' && (
                  <div className="grid grid-cols-3 gap-1 fade-in">
                    {months.map((m, i) => {
                      const isPast = selectedYear === today.getFullYear() && i < today.getMonth();
                      return (
                        <button
                          key={m}
                          disabled={isPast}
                          onClick={() => { setSelectedMonth(i); setCalView('days'); }}
                          className={`picker-cell ${i === selectedMonth ? 'active' : ''} ${isPast ? 'disabled' : ''}`}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                )}

                {calView === 'days' && (
                  <>
                    <div className="grid grid-cols-7 mb-1">
                      {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                        <div key={d} className="text-center f-jost text-[13px] uppercase tracking-widest py-2" style={{ color: GOLD }}>
                          {d}
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7">
                      {getMonthDays(selectedYear, selectedMonth).map((date, i) => {
                        if (!date) return <div key={i} />;
                        const isStart = startDate && date.getTime() === startDate.getTime();
                        const isEnd   = endDate   && date.getTime() === endDate.getTime();
                        const inRange = startDate && endDate && date > startDate && date < endDate;
                        return (
                          <button
                            key={i}
                            onClick={() => handleDateClick(date)}
                            disabled={isDisabled(date)}
                            className={`cal-day ${isStart || isEnd ? 'selected' : inRange ? 'in-range' : ''}`}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                    {startDate && !endDate && (
                      <p className="f-jost text-[13px] text-center mt-4" style={{ color: GOLD }}>
                        Select your return date
                      </p>
                    )}
                    {startDate && endDate && (
                      <button
                        onClick={() => setWhenOpen(false)}
                        className="f-jost w-full text-[13px] font-semibold uppercase tracking-[0.16em] mt-4 pt-3 border-t flex items-center justify-center gap-2 transition-colors"
                        style={{ borderColor: '#f0ece6', color: GOLD }}
                      >
                        Confirm dates <Check size={11} strokeWidth={2.5} />
                      </button>
                    )}
                    {!startDate && (
                      <p className="f-jost text-[13px] text-center mt-4 font-light" style={{ color: '#12100e' }}>
                        Dates are required to continue
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Search CTA ── */}
          <div className="flex items-center p-3">
            <button
              onClick={() => {
                if (selectedCountries.length === 0) { setWhereOpen(true); return; }
                if (!startDate || !endDate)         { setWhenOpen(true);  return; }
                openDrawer();
              }}
              className="search-cta"
              aria-label="Search safaris"
              style={{ opacity: searchReady ? 1 : 0.55 }}
            >
              <Search size={16} strokeWidth={1.5} color="#fff" />
            </button>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          ARCHITECT DRAWER
      ════════════════════════════════════════ */}
      {drawerRendered && (
      <div
        style={{
          position:      props.onClose ? 'absolute' : 'fixed',
          inset:         0,
          zIndex:        props.onClose ? 10 : 9999,
          visibility:    drawerOpen ? 'visible' : 'hidden',
          pointerEvents: drawerOpen ? 'auto' : 'none',
        }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0"
          style={{
            background:    'rgba(8,14,26,0.65)',
            backdropFilter:'blur(6px)',
            opacity:       drawerOpen ? 1 : 0,
            transition:    'opacity 0.4s',
          }}
          onClick={closeDrawer}
        />

        {/* Panel */}
        <div
          className="drawer-panel"
          style={{
            position:  props.onClose ? 'absolute' : 'fixed',
            transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
          }}
        >
          {/* ── Header ── */}
          <div className="relative p-8" style={{ background: NAVY, borderBottom: `1px solid rgba(184,151,90,0.2)` }}>
            <button
              onClick={closeDrawer}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center transition-colors"
              style={{ color: 'rgba(255,255,255,0.35)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <X size={14} strokeWidth={1.5} />
            </button>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px" style={{ background: GOLD }} />
                <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                  Safari Planner
                </span>
              </div>
              <h2 className="f-cormorant text-white font-light leading-none mb-2" style={{ fontSize: '36px' }}>
                Plan your own safari
              </h2>
              <p className="f-jost text-[15px] font-light" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Bespoke journey design
              </p>
            </div>
          </div>

          {/* ── Selection summary ── */}
          {selectedCountries.length > 0 && (
            <div className="px-8 py-5" style={{ background: '#FAFAF8', borderBottom: '1px solid #f0ece6' }}>
              <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}>
                Your Selection
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedCountries.map(c => <span key={c} className="tag-badge">{c}</span>)}
              </div>
              {startDate && (
                <p className="f-jost text-[15px] text-slate-500 font-light">
                  {startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {endDate && ` – ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                </p>
              )}
            </div>
          )}

          {/* ── Form ── */}
          <div className="flex-1 px-8 py-8 overflow-y-auto">
            <p className="f-cormorant text-slate-800 font-light text-[27px] leading-snug mb-2">
              Build it your way.
            </p>
            <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed mb-8">
              Share your details to unlock your personalised, full-screen itinerary builder.
            </p>

            <form onSubmit={handleLaunch} className="space-y-6">

              {/* ── Full Name ── */}
              <div>
                <label className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] block mb-2" style={{ color: '#12100e' }}>
                  Full Name
                </label>
                <div className="relative">
                  <User size={12} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: errors.name ? '#ef4444' : '#94a3b8' }} />
                  <input
                    type="text"
                    placeholder="Eleanor Ashworth"
                    className="drawer-field"
                    style={{ borderBottomColor: errors.name ? '#ef4444' : undefined }}
                    value={userData.name}
                    onChange={e => {
                      setUserData(p => ({ ...p, name: e.target.value }));
                      if (errors.name) setErrors(p => ({ ...p, name: '' }));
                    }}
                  />
                </div>
                {errors.name && <p className="f-jost text-[13px] mt-1" style={{ color: '#ef4444' }}>{errors.name}</p>}
              </div>

              {/* ── Email Address ── */}
              <div>
                <label className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] block mb-2" style={{ color: '#12100e' }}>
                  Email Address
                </label>
                <div className="relative">
                  <Mail size={12} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: errors.email ? '#ef4444' : '#94a3b8' }} />
                  <input
                    type="email"
                    placeholder="eleanor@email.com"
                    className="drawer-field"
                    style={{ borderBottomColor: errors.email ? '#ef4444' : undefined }}
                    value={userData.email}
                    onChange={e => {
                      setUserData(p => ({ ...p, email: e.target.value }));
                      if (errors.email) setErrors(p => ({ ...p, email: '' }));
                    }}
                  />
                </div>
                {errors.email && <p className="f-jost text-[13px] mt-1" style={{ color: '#ef4444' }}>{errors.email}</p>}
              </div>

              {/* ── Phone Number ── */}
              <div>
                <label className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] block mb-2" style={{ color: '#12100e' }}>
                  Phone Number
                </label>
                <div className="relative">
                  <Phone size={12} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: errors.phone ? '#ef4444' : '#94a3b8' }} />
                  <input
                    type="tel"
                    placeholder="+44 7123 456789"
                    className="drawer-field"
                    style={{ borderBottomColor: errors.phone ? '#ef4444' : undefined }}
                    value={userData.phone}
                    onChange={e => {
                      setUserData(p => ({ ...p, phone: e.target.value }));
                      if (errors.phone) setErrors(p => ({ ...p, phone: '' }));
                    }}
                  />
                </div>
                {errors.phone && <p className="f-jost text-[13px] mt-1" style={{ color: '#ef4444' }}>{errors.phone}</p>}
              </div>

              <div className="pt-4">
                <button type="submit" className="drawer-cta">
                  <span>Start planning</span>
                  <ArrowRight size={14} />
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
      )}
    </div>
  );
});

ProfessionalSearchBar.displayName = 'ProfessionalSearchBar';
export default ProfessionalSearchBar;